import { useCallback, useState } from "react";

export interface RecentUser {
  id: number;
  name: string;
  email: string;
}

export const RECENTLY_VIEWED_USERS_STORAGE_KEY = "admin-recentlyViewedUsers";
export const RECENTLY_VIEWED_USER_TOKENS_STORAGE_KEY =
  "admin-recentlyViewedGrants";
const MAX_ENTRIES = 5;

const isRecentUser = (entry: unknown): entry is RecentUser =>
  typeof entry === "object" &&
  entry !== null &&
  typeof (entry as RecentUser).id === "number" &&
  typeof (entry as RecentUser).name === "string" &&
  typeof (entry as RecentUser).email === "string";

const readStorage = (storageKey: string): RecentUser[] => {
  try {
    const raw = window.localStorage.getItem(storageKey);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    // Validate each entry so malformed storage can't crash the UI.
    return parsed.filter(isRecentUser).slice(0, MAX_ENTRIES);
  } catch {
    // ignore malformed / unavailable storage (e.g. private mode)
    return [];
  }
};

/** Clears every recently-viewed list (e.g. on logout, to not retain PII). */
export const clearRecentlyViewedUsers = () => {
  [
    RECENTLY_VIEWED_USERS_STORAGE_KEY,
    RECENTLY_VIEWED_USER_TOKENS_STORAGE_KEY,
  ].forEach((key) => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // ignore unavailable storage
    }
  });
};

/**
 * Tracks the last few users an admin opened, kept in localStorage. Per-browser
 * and per-device only — purely a convenience shortcut, no backend involved. The
 * storage key is a parameter so separate admin surfaces (user management vs.
 * user tokens) keep independent recents.
 */
export const useRecentlyViewedUsers = (
  storageKey: string = RECENTLY_VIEWED_USERS_STORAGE_KEY
) => {
  const [recentUsers, setRecentUsers] = useState<RecentUser[]>(() =>
    readStorage(storageKey)
  );

  const addRecentUser = useCallback(
    (user: RecentUser) => {
      setRecentUsers((previous) => {
        const next = [
          user,
          ...previous.filter((entry) => entry.id !== user.id),
        ].slice(0, MAX_ENTRIES);
        try {
          window.localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          // ignore storage write failures
        }
        return next;
      });
    },
    [storageKey]
  );

  return { recentUsers, addRecentUser };
};
